import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings, GAME_LAYOUT } from "../../../settings";

/**
 * FreeSpinMaskReelController
 *
 * Manages mask spine instances for one reel.
 * Two methods: coverSymbols() and revealSymbols().
 * All symbols animate simultaneously — no stagger, no timeouts.
 */
class FreeSpinMaskReelController {
  constructor(
    maskContainer,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelData,
    freeSpinReelRefs,
    freeSpinOverlayReelRefs
  ) {
    this.maskContainer = maskContainer;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelData = reelData || [];
    this.freeSpinReelRefs = freeSpinReelRefs;
    this.freeSpinOverlayReelRefs = freeSpinOverlayReelRefs;

    this.spineInstances = new Map();
    this.spineContainers = [];

    this._cachedScale = null;
    this._cachedWidth = null;
    this._cachedHeight = null;
    this._destroyed = false;

    this._initializeSpines();
  }

  _initializeSpines() {
    for (let i = 0; i < GAME_LAYOUT.SYMBOLS_VISIBLE; i++) {
      const container = new PIXI.Container();
      container.y = this.symbolHeight * i;
      container.x = 0;
      container.name = `mask_spine_${this.reelIndex}_${i}`;
      this.maskContainer.addChild(container);
      this.spineContainers.push(container);

      const spine = this._createSpineInstance();
      if (!spine) continue;

      spine.position.set(0, 0);
      spine.zIndex = 3;
      spine.alpha = 0.001;

      container.addChild(spine);
      this.spineInstances.set(i, spine);

      this._applyScale(spine);
      this._warmUpSpine(spine);
    }
  }

  _createSpineInstance() {
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has("symbol_flip-skeleton") || !assets.has("symbol_flip-atlas")) return null;

      const spine = Spine.from({
        skeleton: "symbol_flip-skeleton",
        atlas: "symbol_flip-atlas",
        autoUpdate: false,
      });

      if (!spine?.skeleton || !spine?.state) return null;
      return spine;
    } catch {
      return null;
    }
  }

  _warmUpSpine(spine) {
    if (!spine?.state) return;
    try {
      for (const animName of ["flip_in", "flip_out", "flip_idle"]) {
        const hasAnim = spine.skeleton?.data?.animations?.find((a) => a.name === animName);
        if (!hasAnim) continue;
        spine.state.setAnimation(0, animName, false);
        spine.state.update(0.016);
        spine.skeleton.updateWorldTransform();
      }
      spine.alpha = 0.001;
      const container = spine.parent;
      requestAnimationFrame(() => {
        if (this._destroyed) return;
        requestAnimationFrame(() => {
          if (this._destroyed) return;
          requestAnimationFrame(() => {
            if (this._destroyed) return;
            if (spine.state) {
              spine.state.clearTracks();
              spine.autoUpdate = false;
            }
            spine.alpha = 0.001;
            if (container) container.alpha = 0.001;
          });
        });
      });
    } catch {}
  }

  _calculateScale() {
    if (this._cachedScale !== null && this._cachedWidth === this.symbolWidth && this._cachedHeight === this.symbolHeight) {
      return this._cachedScale;
    }
    this._cachedScale = Math.min(
      this.symbolWidth / Settings.SYMBOL_WIDTH,
      this.symbolHeight / Settings.SYMBOL_HEIGHT
    );
    this._cachedWidth = this.symbolWidth;
    this._cachedHeight = this.symbolHeight;
    return this._cachedScale;
  }

  _applyScale(spine) {
    if (!spine) return;
    spine.scale.set(this._calculateScale());
  }

  // --- Public API ---

  updateReelData(newReelData) {
    this.reelData = newReelData;
  }

  resizeSymbol(newWidth, newHeight) {
    if (this.symbolWidth === newWidth && this.symbolHeight === newHeight) return;
    this.symbolWidth = newWidth;
    this.symbolHeight = newHeight;
    this._cachedScale = null;
    for (let i = 0; i < this.spineContainers.length; i++) {
      this.spineContainers[i].y = newHeight * i;
    }
    this.spineInstances.forEach((spine) => this._applyScale(spine));
  }

  /**
   * Cover all symbols at once (flip_in → flip_idle).
   * Resolves when all flip_in complete events fire.
   */
  coverSymbols() {
    return new Promise((resolve) => {
      let done = 0;
      const total = this.spineInstances.size;
      if (total === 0) { resolve(); return; }

      this.spineInstances.forEach((spine) => {
        spine.state.clearListeners();
        spine.autoUpdate = true;
        spine.alpha = 1;
        if (spine.parent) spine.parent.alpha = 1;

        spine.state.setAnimation(0, "flip_in", false);

        spine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name !== "flip_in") return;
            spine.state.setAnimation(0, "flip_idle", true);
            done++;
            if (done === total) resolve();
          },
        });
      });
    });
  }

  /**
   * Cover a single symbol at a specific row (flip_in → flip_idle).
   * Fires onCoverComplete at sym_cover_complete event (~100% of flip_in).
   * Resolves when flip_in complete event fires.
   * @param {number} rowIndex - Row position (0, 1, 2)
   * @param {Function|null} onCoverComplete - Called at sym_cover_complete
   */
  coverSymbolAt(rowIndex, onCoverComplete = null) {
    return new Promise((resolve) => {
      const spine = this.spineInstances.get(rowIndex);
      if (!spine) { resolve(); return; }

      spine.state.clearListeners();
      spine.autoUpdate = true;
      spine.alpha = 1;
      if (spine.parent) spine.parent.alpha = 1;

      spine.state.setAnimation(0, "flip_in", false);

      spine.state.addListener({
        event: (entry, event) => {
          if (event.data.name === "sym_cover_complete" && onCoverComplete) {
            onCoverComplete(rowIndex);
          }
        },
        complete: (entry) => {
          if (entry.animation.name !== "flip_in") return;
          spine.state.setAnimation(0, "flip_idle", true);
          resolve();
        },
      });
    });
  }

  /**
   * Reveal all symbols at once (flip_out → hide).
   * Resolves when all flip_out complete events fire.
   */
  revealSymbols() {
    return new Promise((resolve) => {
      let done = 0;
      const total = this.spineInstances.size;
      if (total === 0) { resolve(); return; }

      this.spineInstances.forEach((spine) => {
        spine.state.clearListeners();
        spine.autoUpdate = true;
        spine.alpha = 1;
        if (spine.parent) spine.parent.alpha = 1;

        spine.state.setAnimation(0, "flip_out", false);

        spine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name !== "flip_out") return;
            spine.alpha = 0.001;
            spine.autoUpdate = false;
            if (spine.parent) spine.parent.alpha = 0.001;
            done++;
            if (done === total) resolve();
          },
        });
      });
    });
  }

  /**
   * Reveal a single symbol at a specific row (flip_out → hide).
   * Fires onReveal at ~80% (sym_reveal) — for starting landing animations.
   * Fires onRevealComplete at ~90% (sym_reveal_complete) — for signaling spin complete.
   * Resolves when flip_out complete event fires.
   * @param {number} rowIndex - Row position (0, 1, 2)
   * @param {Function|null} onReveal - Called at sym_reveal (~80%)
   * @param {Function|null} onRevealComplete - Called at sym_reveal_complete (~90%)
   */
  revealSymbolAt(rowIndex, onReveal = null, onRevealComplete = null) {
    return new Promise((resolve) => {
      const spine = this.spineInstances.get(rowIndex);
      if (!spine) { resolve(); return; }

      spine.state.clearListeners();
      spine.autoUpdate = true;
      spine.alpha = 1;
      if (spine.parent) spine.parent.alpha = 1;

      spine.state.setAnimation(0, "flip_out", false);

      spine.state.addListener({
        event: (entry, event) => {
          if (event.data.name === "sym_reveal" && onReveal) {
            onReveal(rowIndex);
          }
          if (event.data.name === "sym_reveal_complete" && onRevealComplete) {
            onRevealComplete(rowIndex);
          }
        },
        complete: (entry) => {
          if (entry.animation.name !== "flip_out") return;
          spine.alpha = 0.001;
          spine.autoUpdate = false;
          if (spine.parent) spine.parent.alpha = 0.001;
          resolve();
        },
      });
    });
  }

  // --- Cleanup ---

  cleanup() {
    this._destroyed = true;
    this.spineInstances.forEach((spine) => {
      if (spine) {
        if (spine.state) { spine.state.clearTracks(); spine.state.clearListeners(); }
        if (spine.parent) spine.parent.removeChild(spine);
        spine.destroy({ children: true, texture: false, baseTexture: false });
      }
    });
    this.spineInstances.clear();
    this.spineContainers.forEach((c) => {
      if (c.parent) c.parent.removeChild(c);
      c.destroy({ children: true });
    });
    this.spineContainers = [];
  }
}

export default FreeSpinMaskReelController;
